import { userAtom } from '@/atoms/user';
import SwiperBlock from '@/components/pages/buildings/SwiperBlock';
import ParkingRatesDisplay from '@/components/pages/buildings/ParkingRatesDisplay';
import { sendAppierEvent } from '@/utils/appier';
import {
  Box,
  Button,
  Container,
  Flex,
  Text,
  Image,
  Center,
  useBreakpointValue,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  useDisclosure,
} from '@chakra-ui/react';
import { useAtomValue } from 'jotai';
import { useEffect, useState } from 'react';
import StaticMap from '@/components/shared/GoogleStaticMap';
import { Location as LocationIcon } from '@/assets/icons';
import { AiTwotoneCar as CarIcon } from 'react-icons/ai';

const Main = ({ data }) => {
  const user = useAtomValue(userAtom);
  const isMobile = useBreakpointValue({ base: true, md: false });

  useEffect(() => {
    let variables = {};
    if (user) {
      variables = {
        ...variables,
        name: user?.fullname,
        email: user?.email,
        phone: user?.phone,
      };
    }

    // Appier Event 瀏覽場站資訊頁
    sendAppierEvent('building_viewed', {
      user_id: user?.id,
      phone: user?.phone,
      building_name: data.name,
      building_id: data.id,
      building_url: window?.location?.origin + window?.location?.pathname,
      building_city: data.city,
      building_zone: data.zone,
      building_road: data.road,
      building_address: `${data.city}${data.zone}${data.road}`,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const slides = [
    {
      id: 'space_1_image_1',
      name: data?.name,
      url: data?.spaces[0]?.image2,
      entry_url: data?.spaces[0]?.image1,
    },
  ];

  const targetSpace = data.spaces.find((space) => space.manumatic);
  const parkingRate = targetSpace?.parking_rates || [];

  // APP 下載按鈕
  const DownloadAppButton = () => (
    <Button
      w='100%'
      as={'a'}
      href={data?.share_link}
      target='_blank'
      leftIcon={<CarIcon color='white' />}
      my={4}
      bg='primary.blue'
      color='white'
      _hover={{
        bg: 'primary.darkblue',
      }}>
      下載 USPACE APP 停車
    </Button>
  );

  // 月租車位按鈕
  const MonthlyRentalButton = () =>
    data?.subs_url && (
      <Button
        w='100%'
        as={'a'}
        href={data?.subs_url}
        target='_blank'
        leftIcon={<CarIcon color='black' />}
        variant='outline'
        border='1px solid #333333'
        bg='primary.white'>
        查看月租車位
      </Button>
    );

  return (
    <>
      <Flex flex='1' mt='px' direction='column' w='100%'>
        <Box>
          <Container maxW='1120px' w='100%'>
            <Box pt={{ base: 4, lg: 10 }} pb={{ base: 4, lg: 10 }}>
              <SwiperBlock slides={slides} />
            </Box>

            <Flex flexWrap={{ base: 'wrap', md: 'nowrap' }}>
              <Box flex='1 1 auto' w='100%'>
                <h1>
                  <Text
                    fontSize={{ base: '2xl', md: '3xl' }}
                    fontWeight='bold'
                    mt={{ base: 4, md: 0 }}
                    mb={2}>
                    {data?.name}
                  </Text>
                </h1>
                <Box>
                  <Flex
                    color='gray.700'
                    align='center'
                    fontSize={{ base: 'sm', md: 'md' }}>
                    <LocationIcon />
                    <h3>
                      <Text ml='4px'>
                        {data &&
                          ` ${data.city ? data.city : ''}${
                            data.zone ? data.zone : ''
                          } ${data.road ? data.road : ''}`}
                      </Text>
                    </h3>
                  </Flex>
                  <Box my={4}>
                    <StaticMap
                      lat={data?.coordinate?.lat}
                      lng={data?.coordinate?.lon}
                    />
                  </Box>
                  {parkingRate.length > 0 && (
                    <ParkingRatesDisplay
                      parkingRates={parkingRate}
                      buildingName={data?.name}
                      spaceNumber={Math.max(
                        data?.space_number,
                        data?.space_total,
                      )}
                    />
                  )}
                </Box>
              </Box>

              {/* 桌面版側邊欄 */}
              {!isMobile && (
                <Box flex='0 0 350px' ml={4} mt={0}>
                  <Box
                    p={6}
                    borderWidth='1px'
                    borderColor='blackAlpha.200'
                    borderRadius='16px'>
                    <Center>
                      <Image
                        src={data?.share_link_qrcode_image}
                        alt='車位 QR Code'
                        width={240}
                      />
                    </Center>
                    <DownloadAppButton />
                    <MonthlyRentalButton />
                  </Box>
                </Box>
              )}
            </Flex>
          </Container>
        </Box>
      </Flex>

      {/* 手機版固定底部按鈕 */}
      {isMobile && (
        <>
          <Box
            position='fixed'
            bottom={0}
            left={0}
            right={0}
            p={4}
            bg='white'
            borderTopWidth='1px'
            borderTopColor='gray.200'
            zIndex={10}
            boxShadow='0 -2px 10px rgba(0, 0, 0, 0.05)'>
            <Flex justifyContent='space-between' gap={4}>
              {data?.subs_url && (
                <Button
                  flex={1}
                  as={'a'}
                  href={data?.subs_url}
                  leftIcon={<CarIcon color='black' />}
                  variant='outline'
                  border='1px solid #333333'
                  bg='primary.white'
                  fontSize='12px'>
                  月租車位
                </Button>
              )}
              <Button
                flex={1}
                as={'a'}
                href={data?.share_link}
                leftIcon={<CarIcon color='white' />}
                bg='primary.blue'
                color='white'
                _hover={{
                  bg: 'primary.darkblue',
                }}
                fontSize='12px'>
                下載 APP 臨時停車
              </Button>
            </Flex>
          </Box>
        </>
      )}
    </>
  );
};

export default Main;
